import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import productsService from '../services/productsService';
import PricingTab from './PricingTab';
import './ProductPage.css';

export default function ProductPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editName, setEditName] = useState('');
    const [editDisplayName, setEditDisplayName] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [msg, setMsg] = useState(null);
    const [dirty, setDirty] = useState(false);

    useEffect(() => { load(); }, [id]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await productsService.list();
            const found = (res.data?.products || []).find(p => String(p.id) === String(id));
            if (!found) { navigate('/admin/products'); return; }
            setProduct(found);
            setEditName(found.name);
            setEditDisplayName(found.display_name);
            setDirty(false);
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to load product', 'error');
        } finally {
            setLoading(false);
        }
    };

    const flash = (text, type = 'success') => {
        setMsg({ type, text });
        setTimeout(() => setMsg(null), 4000);
    };

    const handleChange = (field, value) => {
        if (field === 'name') setEditName(value);
        if (field === 'display_name') setEditDisplayName(value);
        setDirty(true);
    };

    const save = async () => {
        if (!editName.trim() || !editDisplayName.trim()) return;
        setSaving(true);
        try {
            const res = await productsService.update(id, { name: editName.trim(), display_name: editDisplayName.trim() });
            setProduct(res.data.product);
            setEditName(res.data.product.name);
            setEditDisplayName(res.data.product.display_name);
            setDirty(false);
            flash('Product saved');
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to save product', 'error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete product "${product.display_name}"? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            await productsService.delete(id);
            navigate('/admin/products');
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to delete product', 'error');
            setDeleting(false);
        }
    };

    if (loading) return <div style={{ padding: '2rem' }}>Loading...</div>;
    if (!product) return null;

    return (
        <div className="product-page">
            <div className="product-page-breadcrumb">
                <button className="link-btn" onClick={() => navigate('/admin/products')}>Products</button>
                <span className="breadcrumb-sep">/</span>
                <span>{product.display_name}</span>
            </div>

            {msg && <div className={`msg-banner msg-${msg.type}`}>{msg.text}</div>}

            <div className="product-page-details">
                <div className="product-page-fields">
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={editName}
                            onChange={e => handleChange('name', e.target.value)}
                            placeholder="e.g. vault"
                        />
                        <p className="field-hint">Internal identifier used in the system and Stripe lookup keys.</p>
                    </div>
                    <div className="form-group">
                        <label>Display Name</label>
                        <input
                            type="text"
                            value={editDisplayName}
                            onChange={e => handleChange('display_name', e.target.value)}
                            placeholder="e.g. Vault"
                        />
                    </div>
                </div>
                <div className="product-page-actions">
                    <button className="btn-primary" onClick={save} disabled={saving || !dirty}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button className="btn-danger" onClick={remove} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>

            <div className="product-page-pricing">
                <PricingTab productType={editName} productLabel={editDisplayName} />
            </div>
        </div>
    );
}
